
  import Vue from 'vue';
  import {ItemProps} from 'spin-wheel/dist/spin-wheel-esm.js';
  import {ROULETTE_PRIZE_TYPE_ID} from "@/utils/roluette-prizes";
  import {UserActionTypes} from "@/store/modules/user/actions";
  import {showInterstitial, showRewarded} from "@/services/ads";
  import PwaPrizesListSlider from "@/components/common/PwaPrizesListSlider.vue";
  import pageMixin from '@/mixins/page';
  import { BreadcrumbItems, BreadcrumbItemType } from '@/utils/breadcrumb';
  import PwaCurrency from "@/components/common/PwaCurrency.vue";
  import PwaRoulette from "@/components/PwaRoulette.vue";
  import {CurrencyType} from "@/utils/common";
  import PwaRouletteSuccessModal from "@/components/modals/PwaRouletteSuccessModal.vue";

  const rewardVideoTimeout = 5000;

  type PrizeType = {
    id: number;
    roulette_prize_type_id: number;
    title: string;
    win_description: string;
    amount: number;
  };

  const RouletteBuySpinType = {
    FREE: 'free',
    PROMO: 'promo',
    COINS: 'coins',
  };

  export default Vue.extend({
    name: "Roulette",
    mixins: [pageMixin],
    components: {
      PwaPrizesListSlider,
      PwaCurrency,
      PwaRoulette,
      PwaCountdownMessage: () => import('@/components/common/PwaCountdownMessage.vue'),
      PwaWebAdHalfPageBanner: () => import('@/components/ads/PwaWebAdHalfPageBanner.vue'),
      PwaWebAdBillboardBanner: () => import('@/components/ads/PwaWebAdBillboardBanner.vue'),
      PwaWebAdLargeMobileBanner: () => import('@/components/ads/PwaWebAdLargeMobileBanner.vue'),
      PwaWebAdInlineRectangleBanner: () => import('@/components/ads/PwaWebAdInlineRectangleBanner.vue'),
    },
    data() {
      return {
        freeSpinDisabled: true,
        buySpinDisabled: true,
        buySpinCost: null as number,
        nextPrizeTime: null as string,
        isPremium: false,
        premiumTotalSpins: 3,
        lastBestPrizes: [],
        prizesList: [] as PrizeType[],
        prizes: [] as ItemProps[],
        rouletteSpinning: false,
        rewardDone: false,
        currentPrizeIndex: null as number,
        roulettePrizeProduct: null,
        breadcrumb: [BreadcrumbItems.challenges, {labelId: BreadcrumbItems.roulette.labelId}] as BreadcrumbItemType[],
      };
    },
    mounted(): void {
      this.getRoulettePageData();
    },
    computed: {
      hasEnoughWalletQuantity(): boolean {
        return this.buySpinCost !== null && this.walletQuantity >= this.buySpinCost;
      },
      currency(): CurrencyType {
        return this.$store.state.app.config.currency;
      },
    },
    methods: {
      async getRoulettePageData() {
        const {data}: any = await this.$http.page.getRoulettePage();
        this.updatePrizesList(data.data.roulette_prizes || []);
        this.freeSpinDisabled = data.data.can_free_spin_roulette !== true;
        this.buySpinDisabled = data.data.can_buy_spin_roulette !== true;
        this.buySpinCost = data.data.buy_spin_coins_cost || null;
        this.nextPrizeTime = data.data.next_roulette_prize_time || null;
        this.isPremium = data.data.is_premium;
        this.premiumTotalSpins = data.data.premium_total_spins || 3;
        this.updateLastBestPrizes(data.data.last_best_prizes || []);
      },
      updatePrizesList(prizes: PrizeType[]) {
        const computedStyle = window.getComputedStyle(document.documentElement);
        const labelColor = computedStyle.getPropertyValue('--primary');
        this.prizesList = prizes;
        this.prizes = this.prizesList.map(({roulette_prize_type_id, amount}) => {
          let label = '';
          let image = null;
          let imageScale = 0.4;
          if (roulette_prize_type_id === ROULETTE_PRIZE_TYPE_ID.NO_PRIZE) {
            image = new Image();
            image.src = this.getBrandImageSrc('roulette/roulette-no-prize.png');
          } else {
            const currencyIcon = this.currency.icon;
            label = this.formatPrice(amount, !currencyIcon, 2, true);
            if (currencyIcon) {
              image = new Image();
              image.src = this.getBrandImageSrc(`${currencyIcon}.svg`);
              imageScale = 0.2;
            }
          }
          return {
            label,
            image,
            imageScale,
            labelColor,
          };
        });
      },
      updateLastBestPrizes(lastBestPrizes: any[]) {
        this.lastBestPrizes = lastBestPrizes.map(lastBestPrize => ({
          id: `${lastBestPrize.user_id}_${lastBestPrize.date}`,
          avatar: lastBestPrize.avatar,
          username: lastBestPrize.username,
          quantity: lastBestPrize.amount,
          transaction_date: lastBestPrize.date,
        }));
      },
      onFreeSpinRouletteClick() {
        if (!this.freeSpinDisabled && !this.rouletteSpinning) {
          if (!this.rewardDone) {
            this.rouletteSpinning = true;

            const onRewarded = () => {
              if (!this.rewardDone) {
                this.showLoader(false);
                this.rewardDone = true;
                this.rouletteSpinning = false;
              }
            };

            const onRewardedError = () => {
              showInterstitial(true).then(showInterstitialSuccess => {
                if (showInterstitialSuccess) {
                  onRewarded();
                }
              });

              // Fallback por si acaso tarda mucho
              setTimeout(() => {
                onRewarded();
              }, rewardVideoTimeout);
            };

            showRewarded().then(showRewardedSuccess => {
              if (showRewardedSuccess) {
                onRewarded();
              } else {
                onRewardedError();
              }
            });

            this.showLoader(true);
          } else {
            this.postRoulettePrize();
          }
        }
      },
      onBuySpinRouletteClick() {
        if (!this.buySpinDisabled && !this.rouletteSpinning && this.buySpinCost) {
          this.onBuyRouletteEvent(false);
        }
      },
      onBuyRouletteEvent(promo: boolean) {
        const buyType = promo ? RouletteBuySpinType.PROMO : RouletteBuySpinType.COINS;
        this.postRoulettePrize(buyType);
      },
      async postRoulettePrize(buyType = RouletteBuySpinType.FREE) {
        this.roulettePrizeProduct = null;
        this.rouletteSpinning = true;

        try {
          this.spinRoulette();
          const params = {buyType};
          const {data}: any = await this.$http.user.postRoulettePrize(params);
          this.freeSpinDisabled = data.data.can_free_spin_roulette !== true;
          this.buySpinDisabled = data.data.can_buy_spin_roulette !== true;
          this.buySpinCost = data.data.buy_spin_coins_cost || null;
          this.nextPrizeTime = data.data.next_roulette_prize_time || null;
          this.roulettePrizeProduct = data.data.product || null;
          this.rewardDone = false;
          const prize: PrizeType = data.data.roulette_prize || null;
          if (prize) {
            let prizeIndex = this.prizesList.findIndex(({id}) => id === prize.id);
            if (prizeIndex < 0) {
              this.updatePrizesList(data.data.roulette_prizes || []);
              prizeIndex = this.prizesList.findIndex(({id}) => id === prize.id);
            }

            this.currentPrizeIndex = prizeIndex;
            this.stopRouletteAtPrize(buyType);
          } else {
            this.stopRoulette();
            this.rouletteSpinning = false;
            this.showToastError(this.$t('139'), this.$t('140'));
          }

        } catch (e) {
          this.stopRoulette();
          this.rouletteSpinning = false;
          this.showToastError(this.$t('139'), this.$t('140'));
        }
      },
      spinRoulette() {
        const roulette: any = this.$refs.roulette;
        roulette.spin();
      },
      stopRoulette() {
        const roulette: any = this.$refs.roulette;
        roulette.stop();
      },
      stopRouletteAtPrize(buyType = RouletteBuySpinType.FREE) {
        const roulette: any = this.$refs.roulette;
        roulette.spinToItem(this.currentPrizeIndex, 5000, false, 1)
          .then((success: boolean) => {
            if (success) {
              this.onRouletteSuccess(buyType);
              this.rouletteSpinning = false;
            }
          });
      },
      onRouletteSuccess(buyType = RouletteBuySpinType.FREE) {
        const prize = this.prizesList[this.currentPrizeIndex];
        const {roulette_prize_type_id} = prize;
        const updateWallet = roulette_prize_type_id === ROULETTE_PRIZE_TYPE_ID.COIN ||
          roulette_prize_type_id === ROULETTE_PRIZE_TYPE_ID.PROMO ||
          buyType !== RouletteBuySpinType.FREE;

        if (updateWallet) {
          this.$store.dispatch(UserActionTypes.UPDATE_USER_WALLET);
        }

        const title = roulette_prize_type_id === ROULETTE_PRIZE_TYPE_ID.NO_PRIZE ? this.$t('664') : this.$t('665');
        const message = this.$createElement(PwaRouletteSuccessModal, {
          props: {
            prize,
          },
        });

        this.showModal({
          title,
          message,
          hideCancel: true,
          noCloseOnBackdrop: true,
          noCloseOnEsc: true,
        });
      },
    },
  });
